import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Footprints, X } from "lucide-react";
import T from "@/components/T";

export default function PartyView({ ch, setCh }) {
  const { t } = useTranslation();
  const [mine, setMine] = useState(null);
  const [open, setOpen] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setMine(await api.partyMine());
    setOpen(await api.partyOpen());
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, []);

  const create = async () => {
    setBusy(true);
    try { await api.partyCreate(); await refresh(); toast.success(t("party.toast.formed", "Party formed")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed", "Failed")); }
    finally { setBusy(false); }
  };
  const join = async (pid) => {
    setBusy(true);
    try { await api.partyJoin(pid); await refresh(); toast.success(t("party.toast.joined", "Joined party")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed", "Failed")); }
    finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true);
    try { await api.partyLeave(); await refresh(); toast(t("party.toast.left", "You leave the road behind")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed", "Failed")); }
    finally { setBusy(false); }
  };

  if (mine) {
    const partner = (mine.members_info || []).find(m => m.name !== ch.name);
    return (
      <div className="space-y-2">
        <div className="panel p-3" data-testid="my-party">
          <div className="flex items-center gap-2 mb-2">
            <Footprints className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} />
            <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]"><T>Your Party</T></div>
          </div>
          <div className="space-y-1">
            {(mine.members_info || []).map(m => (
              <div key={m.user_id} className="panel-inner p-2 flex justify-between items-center" data-testid={`party-member-${m.user_id}`}>
                <div>
                  <div className="font-heading text-sm text-[#E2DFD8]">
                    {m.name} {m.user_id === mine.leader_id && <span className="text-[#B89C3A] text-[10px] ml-1">LEADER</span>}
                  </div>
                  <div className="font-mono text-[10px] text-[#8c8c8c]">Lv {m.level} · {m.class}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] italic text-[#666]">
            {partner
              ? "You walk together. Every step's gold and xp is fully shared."
              : "You wait for a companion. Anyone can join your party from the list."}
          </div>
          <button
            data-testid="party-leave"
            onClick={leave}
            disabled={busy}
            className="mt-3 w-full py-2 border border-[#8A0303] text-[#E33333] font-heading text-xs tracking-widest uppercase"
          >
            <X className="w-3 h-3 inline mr-1" strokeWidth={2} /> Leave Party
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="panel p-3">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} /> Travel Parties
        </div>
        <p className="italic text-[10px] text-[#666] mb-2">
          Walk with one other hero. Every gold and xp from a travel step is shared with your companion.
        </p>
        <button
          data-testid="party-create"
          onClick={create}
          disabled={busy}
          className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase"
        >
          Form a Party
        </button>
      </div>
      <div className="panel p-2" data-testid="party-open-list">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1">
          Open Parties ({open.length})
        </div>
        {open.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-2">Nobody is waiting for a companion.</p>
        )}
        {open.map(p => (
          <div key={p.party_id} className="panel-inner p-2 mb-1 flex justify-between items-center" data-testid={`party-${p.party_id}`}>
            <div>
              <div className="font-heading text-sm text-[#E2DFD8]">{p.leader_name}</div>
              <div className="font-mono text-[10px] text-[#8c8c8c]">Lv {p.leader_level} · {p.leader_class}</div>
            </div>
            <button
              data-testid={`party-join-${p.party_id}`}
              onClick={() => join(p.party_id)}
              disabled={busy}
              className="px-3 py-1 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase"
            >
              Join
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
